import { TMappedPlugin } from 'hooks/useContextPlugins';
import { TemplatePayloadData } from './types';
import { safelySplitCtxString } from 'helpers/rule.helper';

/**
 * Serializes a Twilio/Sendgrid TemplatePayloadData object into a flat map of placeholder keys to template strings.
 *
 * @param placeholderKeys - Array of placeholder keys that should be present in the output.
 * @param currentTemplateData - The in-memory TemplatePayloadData to serialize into strings.
 * @returns A record mapping every placeholder key to its serialized string representation.
 */
export const serializeTemplatePayloadData = (placeholderKeys: string[], currentTemplateData: TemplatePayloadData) => {
  const allKeys = [...new Set([...placeholderKeys, ...Object.keys(currentTemplateData)])].sort();
  return allKeys.reduce((acc, key) => {
    const value = currentTemplateData[key];
    if (!value) {
      acc[key] = '';
    } else {
      const params = value.attributeParameters?.map((p) => p.value).join('.');
      const attribute = value.attribute.match(/{{(.*?)}}/)?.[1];

      // only place ctx.attrs inside curlybraces
      const body = attribute
        ? `{{${attribute}${value.attributeMapKey ? `.${value.attributeMapKey}` : ''}${params ? `.${params}` : ''}${
            // Transient/incoming-context mappings never carry a default value
            value.useIncomingContext ? '' : value.attributeDefaultValue ? `,${value.attributeDefaultValue}` : ''
          }}}`
        : value.attribute;

      acc[key] = value.useIncomingContext ? `incomingContext${body}` : body;
    }
    return acc;
  }, {} as Record<string, string>);
};

/**
 * Derives a Twilio/Sendgrid TemplatePayloadData object from an external flat mapping of placeholder keys to template strings.
 * Transforms ctx strings into structured attribute definitions.
 *
 * @param placeholderKeys - Array of placeholder keys to ensure are present in the returned payload.
 * @param externalTemplatePayloadData - External mapping of placeholder keys to template strings.
 * @param attributes - Available attribute definitions used to parse and resolve attribute strings.
 * @returns A TemplatePayloadData object reconstructed from the external template payload.
 */
export const deriveTemplatePayloadData = (
  placeholderKeys: string[],
  externalTemplatePayloadData: Record<string, string>,
  attributes: TMappedPlugin[],
) => {
  const allKeys = [...new Set([...placeholderKeys, ...Object.keys(externalTemplatePayloadData)])].sort();
  return allKeys.reduce((acc, key) => {
    const value = externalTemplatePayloadData[key];
    // Exclude 'languageCode' and email provider attributes `{{{ ... }}}` from the email template payload
    if (key === 'languageCode' || (typeof value === 'string' && value.startsWith('{{{') && value.endsWith('}}}'))) {
      return acc;
    }

    if (!value) {
      acc[key] = {
        attribute: '',
        attributeParameters: [],
      };
      return acc;
    }

    // Transient/incoming-context mappings are serialized with an `incomingContext` prefix.
    // Strip it before parsing so the rest of the logic operates on the `{{...}}` body.
    const useIncomingContext = value.startsWith('incomingContext{{');
    const ctxString = useIncomingContext ? value.slice('incomingContext'.length) : value;

    // an approximation of the attributes - does not account for types or names.
    const attribute = ctxString.match(/{{(.*?)}}/)?.[1];
    if (!attribute) {
      acc[key] = {
        attribute: ctxString,
        attributeParameters: [],
      };
      return acc;
    }
    const attributeProps = safelySplitCtxString(attribute, attributes);
    acc[key] = {
      attribute: `{{${attributeProps?.attributeId || attribute}}}`,
      attributeParameters: attributeProps?.attributeParameters || [],
      attributeMapKey: attributeProps?.attributeMapKey,
      attributeDefaultValue: attributeProps?.defaultValue,
      useIncomingContext,
    };
    return acc;
  }, {} as TemplatePayloadData);
};
